import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { db } from "../../utils/firebase";
import { useApp } from "../../contexts/AppContext";

import ChatMessageBubble from "./ChatMessageBubble";
import { useChatMessages } from "./useChatMessages";

const chatMessagesRef = collection(db, "chatMessages");
const PAGE_SIZE = 100;

function Chat({ onClose }: { onClose: () => void }) {
  const {
    state: { user, event, party, partyUser, selectedPartyId },
  } = useApp();

  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const {
    messages: rawMessages,
    loading,
    hasMore,
    error,
  } = useChatMessages(selectedPartyId, pageSize);

  // Buffer the displayed messages so the list never visibly empties during
  // the `useCollection` re-subscribe window (between bumping `pageSize` and
  // the new snapshot arriving).
  const [displayedMessages, setDisplayedMessages] = useState<ChatMessage[]>([]);
  useEffect(() => {
    if (rawMessages.length > 0) setDisplayedMessages(rawMessages);
  }, [rawMessages]);
  const messages = rawMessages.length > 0 ? rawMessages : displayedMessages;

  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [hasUnreadBelow, setHasUnreadBelow] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const hasInitiallyScrolledRef = useRef(false);
  // Anchor for restoring scroll position after a "load older" completes.
  // We pin to a specific message element (the first one that was on screen),
  // not to a raw scrollTop, because the user may keep scrolling between
  // triggering the load and the new snapshot landing.
  const anchorMessageIdRef = useRef<string | null>(null);
  const anchorOffsetRef = useRef<number>(0);
  const preLoadCountRef = useRef<number>(0);
  const prevLastIdRef = useRef<string | undefined>(undefined);

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  const loadMore = () => {
    if (isLoadingMore || loading || !hasMore) return;
    const el = scrollRef.current;
    if (el) {
      // Pick the first message currently in view as the anchor. After the
      // older messages load, we'll scroll so this same message stays at the
      // same offset from the top of the viewport.
      const containerTop = el.getBoundingClientRect().top;
      const bubbles = el.querySelectorAll<HTMLElement>("[data-message-id]");
      let anchor: HTMLElement | null = null;
      for (const b of Array.from(bubbles)) {
        if (b.getBoundingClientRect().bottom > containerTop) {
          anchor = b;
          break;
        }
      }
      if (anchor) {
        anchorMessageIdRef.current =
          anchor.getAttribute("data-message-id") ?? null;
        anchorOffsetRef.current =
          anchor.getBoundingClientRect().top - containerTop;
      } else {
        anchorMessageIdRef.current = null;
      }
    }
    preLoadCountRef.current = messages.length;
    // User is paging history — they're definitely not at the bottom.
    isAtBottomRef.current = false;
    setIsLoadingMore(true);
    setPageSize((p) => p + PAGE_SIZE);
  };

  const handleScroll = () => {
    // Ignore scroll events fired by the transient shrink during a re-subscribe.
    // While loading more, the DOM may briefly shrink and the browser clamps
    // scrollTop, firing a scroll event with a misleadingly small content size.
    if (isLoadingMore) return;
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom <= 50;
    isAtBottomRef.current = atBottom;
    if (atBottom) setHasUnreadBelow(false);

    if (el.scrollTop <= 80) {
      loadMore();
    }
  };

  // Restore scroll position synchronously after older messages are prepended,
  // so the viewport doesn't jump. Only fires once the message count has
  // actually grown beyond what we had before the load — useCollection
  // briefly empties `value` while re-subscribing, and we must not finalize
  // on that transient empty state. We pin to the same message element the
  // user was viewing, not to a raw scrollTop (which can go stale if the
  // user keeps scrolling between the trigger and the snapshot landing).
  useLayoutEffect(() => {
    if (!isLoadingMore) return;
    if (messages.length <= preLoadCountRef.current) return;
    const el = scrollRef.current;
    const anchorId = anchorMessageIdRef.current;
    if (!el) return;
    if (anchorId) {
      const anchor = el.querySelector<HTMLElement>(
        `[data-message-id="${anchorId}"]`
      );
      if (anchor) {
        const containerTop = el.getBoundingClientRect().top;
        const desiredOffset = anchorOffsetRef.current;
        const currentOffset =
          anchor.getBoundingClientRect().top - containerTop;
        el.scrollTop += currentOffset - desiredOffset;
      }
    }
    anchorMessageIdRef.current = null;
    setIsLoadingMore(false);
  }, [messages, isLoadingMore]);

  // Auto-scroll to bottom on first render, and on a newly-arrived message
  // only if the user was already pinned to the bottom (otherwise surface a
  // "new messages" notification). Skip while loading older history. Uses
  // useLayoutEffect to avoid a paint flash, plus a follow-up rAF to re-pin
  // after late layout (e.g. font/flag loads) settles.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const lastId = messages[messages.length - 1]?._docId;
    const prevLastId = prevLastIdRef.current;
    prevLastIdRef.current = lastId;

    const pinToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };

    if (!hasInitiallyScrolledRef.current) {
      if (messages.length === 0) return;
      pinToBottom();
      hasInitiallyScrolledRef.current = true;
      // Re-pin across the next two frames in case fonts/flag images shift
      // layout after we've measured scrollHeight.
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        pinToBottom();
        raf2 = requestAnimationFrame(pinToBottom);
      });
      return () => {
        cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
      };
    }
    if (isLoadingMore) return;

    const newestChanged = lastId !== prevLastId;
    if (!newestChanged) {
      // Newest id unchanged but the array still changed — typically a
      // cache→server snapshot transition that prepended older messages.
      // If the user is still pinned to the bottom, keep them there;
      // otherwise leave their scroll position alone.
      if (isAtBottomRef.current) pinToBottom();
      return;
    }

    if (isAtBottomRef.current) {
      pinToBottom();
    } else {
      setHasUnreadBelow(true);
    }
  }, [messages, isLoadingMore]);

  // Mark the newest message as seen whenever the message list updates while open
  useEffect(() => {
    if (!partyUser?._docId || messages.length === 0) return;
    const newest = messages[messages.length - 1];
    if (!newest?.created) return;
    updateDoc(doc(db, "partyUsers", partyUser._docId), {
      lastSeenMessageAt: newest.created,
    }).catch(() => {
      // best-effort; ignore failures
    });
  }, [messages, partyUser?._docId]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (
      !trimmed ||
      !user ||
      !event ||
      !selectedPartyId ||
      !partyUser ||
      sending
    )
      return;
    setSending(true);
    try {
      await addDoc(chatMessagesRef, {
        partyId: selectedPartyId,
        eventId: event._docId,
        userId: user.uid,
        userName: partyUser.name,
        text: trimmed,
        created: serverTimestamp(),
      });
      setText("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-esc-purple-900 text-white">
      <header className="flex items-center gap-3 p-4 bg-gradient-to-b from-black via-black/70 to-black/0">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chat"
          className="inline-flex items-center gap-1 border border-white/40 px-2 py-1 text-sm font-montserrat bg-white/20 hover:bg-white/40"
        >
          <span aria-hidden="true">←</span>
        </button>
        <h1 className="text-lg font-montserrat font-semibold leading-none">
          {party?.name ? `${party.name} chat` : "Party chat"}
        </h1>
      </header>

      <div className="relative flex-1 min-h-0">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="absolute inset-0 overflow-y-auto px-3 py-4 flex flex-col"
        >
          {error && (
            <p className="text-center text-esc-red-300 text-sm mb-3 break-words">
              Chat error: {error.message}
            </p>
          )}
          {!isLoadingMore && !hasMore && messages.length > 0 && (
            <p className="text-center text-esc-neutral-400 italic text-xs mb-3">
              Beginning of conversation
            </p>
          )}
          {!loading && messages.length === 0 && (
            <p className="text-center text-esc-neutral-300 italic mt-8">
              No messages yet — say hi!
            </p>
          )}
          {messages.map((m) => (
            <ChatMessageBubble
              key={m._docId}
              message={m}
              isOwn={m.userId === user?.uid}
            />
          ))}
        </div>
        {isLoadingMore && (
          <div
            aria-live="polite"
            className="absolute left-1/2 top-2 -translate-x-1/2 px-3 py-1 bg-black/60 text-esc-neutral-100 text-xs italic rounded-full pointer-events-none"
          >
            Loading older messages…
          </div>
        )}
        {hasUnreadBelow && (
          <button
            type="button"
            onClick={() => {
              scrollToBottom();
              setHasUnreadBelow(false);
            }}
            className="absolute left-1/2 bottom-3 -translate-x-1/2 inline-flex items-center gap-2 px-4 py-1.5 bg-esc-pink-500 text-white text-sm font-montserrat font-bold rounded-full shadow-lg shadow-black/40 hover:bg-esc-pink-400"
          >
            New messages
            <span aria-hidden="true">↓</span>
          </button>
        )}
      </div>

      <form
        onSubmit={handleSend}
        className="flex items-center gap-2 p-3 border-t border-white/10 bg-black/30"
      >
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          className="flex-1 px-3 py-2 text-base text-esc-neutral bg-white rounded-full focus:outline-none"
          disabled={sending}
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          className="px-4 py-2 bg-esc-pink-500 text-white font-montserrat font-bold rounded-full disabled:opacity-30"
        >
          Send
        </button>
      </form>
    </div>
  );
}

export default Chat;
